/**
 * T-009 — Pre-flight locate: the Word-facing seam around the pure `planPreflight` brain.
 *
 * Reads only. Runs every op's `Range.search` in a batch (one `context.sync`, not one-per-op), feeds
 * the match counts into `planPreflight`, and — only if the whole set resolves — hands back the
 * chosen Word `Range` proxies (registered as tracked objects so they survive into the apply pass).
 * If anything fails to locate, it returns the failure and mutates nothing: the paragraph is left
 * byte-identical. Verified in real Word (Stage 4 is manual by design).
 *
 * Table cells: `getRange('Whole').search()` on Mac Word returns 0 hits even when the text is in the
 * cell (the Whole range includes the end-of-cell marker). Fallbacks, in order:
 *   1. search the Content range (excludes the cell/paragraph mark)
 *   2. search `document.body` and keep hits whose `compareLocationWith` the paragraph is Inside
 *
 * Body paragraphs still use Whole search first — the proven path. Fallbacks run only when that
 * returns 0, so they cannot change a successful body-paragraph apply.
 *
 * `Word` is referenced only inside functions, so importing this never breaks the Vitest environment.
 */

import { LocatedOp, isWordSearchable } from './locateOps';
import { planPreflight, PreflightPlan, MatchCounts } from './preflightPlan';
import { isFaithfulSearchHit, isSearchHitInsideParagraph } from './searchHitLocation';
import { logQuickResponseStage } from '../../utils/quickResponseDebug';

/* global Word */

/** Same options for every search so occurrence indices stay comparable. */
const SEARCH_OPTS = { matchCase: true, matchWholeWord: false };

/** A resolved op paired with the live Word range the executor will act on (null for start-inserts). */
export interface ResolvedRange {
  opIndex: number;
  op: LocatedOp;
  /** The chosen search match, tracked; null when the op needs no search (insert at paragraph start). */
  range: Word.Range | null;
}

export interface PreflightLocateResult {
  plan: PreflightPlan;
  /** Populated only when `plan.ok`; ordered right-to-left, matching `plan.resolved`. */
  ranges: ResolvedRange[];
}

/**
 * The search value an op needs located (delete → its value; insert → its anchor text; null when no
 * search is required).
 */
function searchValueOf(op: LocatedOp): string | null {
  if (op.kind === 'delete') return op.value;
  if (op.kind === 'fieldDelete') return null; // resolved by field range, not search
  return op.anchor.type === 'after' || op.anchor.type === 'before' ? op.anchor.value : null;
}

type RangeSearchFn = (value: string) => Word.RangeCollection;

async function searchDistinctValues(
  context: Word.RequestContext,
  startSearch: RangeSearchFn,
  values: string[],
): Promise<Map<string, Word.Range[]>> {
  const out = new Map<string, Word.Range[]>();
  const unique = [...new Set(values.filter((v) => v.length > 0))];
  if (unique.length === 0) return out;

  const runBatch = async (batch: string[]): Promise<boolean> => {
    const collByValue = new Map<string, Word.RangeCollection>();
    for (const value of batch) {
      const coll = startSearch(value);
      coll.load('items');
      collByValue.set(value, coll);
    }
    await context.sync();
    for (const [value, coll] of collByValue) {
      out.set(value, [...(coll.items ?? [])]);
    }
    return true;
  };

  try {
    await runBatch(unique);
    return out;
  } catch {
    // One rejected search term (wildcards / too long) must not sink the rest.
    for (const value of unique) {
      if (out.has(value)) continue;
      try {
        await runBatch([value]);
      } catch {
        out.set(value, []);
      }
    }
    return out;
  }
}

function missingValues(rangesByValue: Map<string, Word.Range[]>, wanted: string[]): string[] {
  return [...new Set(wanted.filter((v) => (rangesByValue.get(v)?.length ?? 0) === 0))];
}

function mergeHits(rangesByValue: Map<string, Word.Range[]>, incoming: Map<string, Word.Range[]>): void {
  for (const [value, ranges] of incoming) {
    if (ranges.length > 0 && (rangesByValue.get(value)?.length ?? 0) === 0) {
      rangesByValue.set(value, ranges);
    }
  }
}

async function filterHitsToParagraph(
  context: Word.RequestContext,
  paragraphRange: Word.Range,
  hitsByValue: Map<string, Word.Range[]>,
): Promise<Map<string, Word.Range[]>> {
  const pending: Array<{
    value: string;
    range: Word.Range;
    loc: OfficeExtension.ClientResult<Word.LocationRelation>;
  }> = [];
  for (const [value, ranges] of hitsByValue) {
    for (const range of ranges) {
      pending.push({ value, range, loc: range.compareLocationWith(paragraphRange) });
    }
  }
  if (pending.length === 0) return new Map();
  await context.sync();

  const filtered = new Map<string, Word.Range[]>();
  for (const item of pending) {
    if (!isSearchHitInsideParagraph(String(item.loc.value ?? ''))) continue;
    const list = filtered.get(item.value) ?? [];
    list.push(item.range);
    filtered.set(item.value, list);
  }
  return filtered;
}

/** Drop RTL/search hits whose live range is longer than the needle. */
async function keepFaithfulHits(
  context: Word.RequestContext,
  hitsByValue: Map<string, Word.Range[]>,
): Promise<Map<string, Word.Range[]>> {
  const pending: Array<{ value: string; range: Word.Range }> = [];
  for (const [value, ranges] of hitsByValue) {
    for (const range of ranges) {
      range.load('text');
      pending.push({ value, range });
    }
  }
  if (pending.length === 0) return hitsByValue;
  await context.sync();

  const faithful = new Map<string, Word.Range[]>();
  const add = (value: string, range: Word.Range): void => {
    const list = faithful.get(value) ?? [];
    list.push(range);
    faithful.set(value, list);
  };
  let rejected = 0;
  const toShrink: Array<{ value: string; range: Word.Range; from: 'start' | 'end' }> = [];
  for (const { value, range } of pending) {
    const raw = range.text || '';
    if (isFaithfulSearchHit(value, raw)) {
      add(value, range);
      continue;
    }
    // Comment ranges / RTL search often return a hit that STARTS (or ends) at the
    // needle and then swallows the rest of the paragraph. Shrink to the needle
    // instead of dropping the op (that aborted the המבטחת plan as occurrence_missing).
    if (raw.startsWith(value)) {
      toShrink.push({ value, range, from: 'start' });
    } else if (raw.endsWith(value)) {
      toShrink.push({ value, range, from: 'end' });
    } else {
      rejected += 1;
    }
  }
  if (toShrink.length > 0) {
    const slimPending: Array<{ value: string; range: Word.Range }> = [];
    for (const { value, range, from } of toShrink) {
      const slim =
        from === 'start' ? range.getRange('Start') : range.getRange('End');
      const movable = slim as unknown as {
        moveEnd?: (u: string, n: number) => void;
        moveStart?: (u: string, n: number) => void;
      };
      if (from === 'start') movable.moveEnd?.('Character', value.length);
      else movable.moveStart?.('Character', -value.length);
      slim.load('text');
      slimPending.push({ value, range: slim });
    }
    await context.sync();
    let recovered = 0;
    for (const { value, range } of slimPending) {
      if (isFaithfulSearchHit(value, range.text || '')) {
        add(value, range);
        recovered += 1;
      } else {
        rejected += 1;
      }
    }
    logQuickResponseStage('preflightLocate: shrunk oversized search hits', {
      attempted: toShrink.length,
      recovered,
    });
  }
  if (rejected > 0) {
    logQuickResponseStage('preflightLocate: rejected oversized search hits', {
      rejected,
      kept: [...faithful.values()].reduce((n, r) => n + r.length, 0),
    });
  }
  return faithful;
}

/**
 * Resolve all op target ranges read-only, or abort. Must be called inside a `Word.run`.
 *
 * @param context   active request context
 * @param paragraph target paragraph
 * @param located   located ops from T-008
 */
export async function preflightLocate(
  context: Word.RequestContext,
  paragraph: Word.Paragraph,
  located: LocatedOp[],
): Promise<PreflightLocateResult> {
  const whole = paragraph.getRange('Whole');

  // An op is "unsearchable" if its value is oversize or contains characters Range.search rejects.
  // We must NOT hand such a value to Word.search (it throws SearchStringInvalidOrTooLong and blows
  // up the whole batch); instead we mark it so planPreflight fails it cleanly.
  const unsearchable = located.map((op) => {
    const value = searchValueOf(op);
    if (value === null) return false; // no search needed (start-insert)
    if (op.kind === 'delete' && op.oversize) return true;
    return !isWordSearchable(value);
  });

  const wantedValues = located
    .map((op, i) => (unsearchable[i] ? null : searchValueOf(op)))
    .filter((v): v is string => v !== null && v.length > 0);

  const rangesByValue = await searchDistinctValues(
    context,
    (value) => whole.search(value, SEARCH_OPTS),
    wantedValues,
  );
  const localHitCount = [...rangesByValue.values()].reduce((n, r) => n + r.length, 0);

  let stillMissing = missingValues(rangesByValue, wantedValues);
  if (stillMissing.length > 0) {
    try {
      const content = paragraph.getRange('Content');
      const contentHits = await searchDistinctValues(
        context,
        (value) => content.search(value, SEARCH_OPTS),
        stillMissing,
      );
      mergeHits(rangesByValue, contentHits);
    } catch {
      // Content range is unavailable on some hosts — body.search fallback still runs.
    }
    stillMissing = missingValues(rangesByValue, wantedValues);
  }

  if (stillMissing.length > 0) {
    const bodySearchable = stillMissing.filter((v) => v.length >= 4);
    const bodyHits = await searchDistinctValues(
      context,
      (value) => context.document.body.search(value, SEARCH_OPTS),
      bodySearchable,
    );
    try {
      const inParagraph = await filterHitsToParagraph(context, whole, bodyHits);
      mergeHits(rangesByValue, inParagraph);
    } catch {
      // compareLocationWith can throw on some table ranges; keep unfiltered body hits only when
      // there is exactly one document match (unambiguous).
      for (const [value, ranges] of bodyHits) {
        if (ranges.length === 1 && (rangesByValue.get(value)?.length ?? 0) === 0) {
          rangesByValue.set(value, ranges);
        }
      }
    }
  }

  const afterFallbackMissing = missingValues(rangesByValue, wantedValues);
  if (localHitCount === 0 && wantedValues.length > 0) {
    logQuickResponseStage('preflightLocate: search fallback', {
      wanted: wantedValues.length,
      localHits: localHitCount,
      resolvedAfterFallback: wantedValues.length - afterFallbackMissing.length,
      stillMissing: afterFallbackMissing.length,
    });
  }

  const faithfulHits = await keepFaithfulHits(context, rangesByValue);

  // Match count per op (aligned to `located`), then the pure decision.
  const counts: MatchCounts = located.map((op, i) => {
    if (unsearchable[i]) return 0;
    const value = searchValueOf(op);
    if (value === null) return null; // start-insert: no search needed
    return faithfulHits.get(value)?.length ?? 0;
  });

  const plan = planPreflight(located, counts, unsearchable);

  // Bind every RESOLVED op to its live range (skipped ops are simply absent). The engine
  // applies this set only when preflight is fully ok — otherwise it falls back or no-ops.
  // Bind each resolution to its live range and keep the proxy alive for the apply pass.
  const ranges: ResolvedRange[] = plan.resolved.map((res) => {
    const value = searchValueOf(res.op);
    let range: Word.Range | null = null;
    if (value !== null && res.matchIndex !== null) {
      range = faithfulHits.get(value)![res.matchIndex];
      // Keep the proxy alive across syncs into the apply pass. Cast sidesteps a known office-js
      // typings quirk where Word.Range isn't recognized as an OfficeExtension.ClientObject.
      (context.trackedObjects.add as (o: Word.Range) => void)(range);
    }
    return { opIndex: res.opIndex, op: res.op, range };
  });

  return { plan, ranges };
}
